<div
    class="view-new-report-page"
    [class.view-new-report-page--presenting]="presenting"
    *transloco="let t"
>
    <div *ngIf="loading" class="view-new-report-loading">
        <mat-spinner diameter="48"></mat-spinner>
    </div>

    <div *ngIf="!loading && loadError" class="view-new-report-error">
        <p>{{ t(loadError) }}</p>
    </div>

    <!--
        `allow-same-origin` is required, not optional. The report is loaded as a
        `blob:` URL this app creates, and the component reaches into the frame's
        `contentDocument` to wire section navigation (`wireIframeNavigation`).
        Dropping it gives the frame an opaque origin, which makes the blob load
        fail and turns the same-origin probe in `assignIframeUrl` into a thrown
        SecurityError - the report renders as a blank page. A blob: URL already
        inherits this app's origin, so listing the token grants nothing the frame
        did not already have; the sandbox still withholds top-level navigation,
        downloads, modals and pointer lock.
    -->
    <iframe
        #previewFrame
        *ngIf="showPreview"
        class="view-new-report-frame"
        [title]="t('Report preview')"
        frameborder="0"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
    ></iframe>

    <div
        *ngIf="translating"
        class="view-new-report-loading view-new-report-overlay"
    >
        <div class="view-new-report-translating">
            <mat-spinner diameter="48"></mat-spinner>
            <p class="view-new-report-translating-text">
                {{ t(getTranslatingMessage()) }}
            </p>
        </div>
    </div>

    <!--
        Presenter chrome. Drawn by THIS document rather than injected into the
        report, so the controls keep the app's design system, its translations and
        its RTL handling, and so nothing a model wrote can style or intercept them.
        The report frame only ever receives one stylesheet and one attribute.
    -->
    <div
        *ngIf="presenting"
        class="report-present"
        role="group"
        [attr.aria-label]="t('Presentation controls')"
    >
        <!-- Announces each step to a screen reader. `polite` because a slide change
             is context, not an interruption, and the section heading is the whole
             announcement a listener needs. -->
        <p class="sr-only" aria-live="polite">
            {{ t('Section') }} {{ slideIndex + 1 }} {{ t('of') }} {{ slides.length }}:
            {{ currentSlideTitle }}
        </p>

        <div class="report-present__progress" aria-hidden="true">
            <span class="report-present__progress-fill" [style.width.%]="slideProgress"></span>
        </div>

        <div class="report-present__bar">
            <button
                type="button"
                class="report-present__btn"
                [attr.aria-label]="t('Previous section')"
                [matTooltip]="t('Previous section')"
                [disabled]="slideIndex === 0"
                (click)="previousSlide()"
            >
                <mat-icon svgIcon="heroicons_outline:chevron-left"></mat-icon>
            </button>

            <button
                type="button"
                class="report-present__title"
                [matMenuTriggerFor]="slideMenu"
                [matTooltip]="t('Jump to section')"
            >
                <span class="report-present__counter">
                    {{ slideIndex + 1 }} / {{ slides.length }}
                </span>
                <span class="report-present__name">{{ currentSlideTitle }}</span>
                <mat-icon
                    class="report-present__chevron icon-size-4"
                    svgIcon="heroicons_mini:chevron-down"
                ></mat-icon>
            </button>

            <button
                type="button"
                class="report-present__btn"
                [attr.aria-label]="t('Next section')"
                [matTooltip]="t('Next section')"
                [disabled]="slideIndex >= slides.length - 1"
                (click)="nextSlide()"
            >
                <mat-icon svgIcon="heroicons_outline:chevron-right"></mat-icon>
            </button>

            <span class="report-present__divider" aria-hidden="true"></span>

            <button
                type="button"
                class="report-present__btn"
                [attr.aria-label]="isFullscreen ? t('Exit full screen') : t('Full screen')"
                [matTooltip]="(isFullscreen ? t('Exit full screen') : t('Full screen')) + ' (F)'"
                (click)="toggleFullscreen()"
            >
                <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
            </button>

            <button
                type="button"
                class="report-present__btn report-present__btn--exit"
                [attr.aria-label]="t('Exit presentation')"
                [matTooltip]="t('Exit presentation') + ' (Esc)'"
                (click)="stopPresenting()"
            >
                <mat-icon svgIcon="heroicons_outline:x-mark"></mat-icon>
            </button>
        </div>

        <mat-menu #slideMenu="matMenu" class="sai-menu report-present__menu" yPosition="above">
            <button
                *ngFor="let slide of slides; let i = index"
                type="button"
                mat-menu-item
                class="report-present__menu-item"
                [class.report-present__menu-item--active]="i === slideIndex"
                [attr.aria-current]="i === slideIndex ? 'true' : null"
                (click)="goToSlide(i)"
            >
                <span class="report-present__menu-index">{{ i + 1 }}</span>
                <span class="report-present__menu-name">{{ slide.title }}</span>
            </button>
        </mat-menu>
    </div>

    <ng-container *ngIf="showPreview">
        <div class="view-new-report-actions" [class.hidden]="presenting">
            <button
                *ngIf="canPresent"
                type="button"
                class="report-action-btn"
                [matTooltip]="t('Present full screen')"
                [attr.aria-label]="t('Present full screen')"
                (click)="startPresenting()"
            >
                <mat-icon class="icon-size-5 text-gray-100">slideshow</mat-icon>
            </button>
            <button
                type="button"
                class="report-action-btn"
                [matTooltip]="t('Translate report') + ' (' + currentLanguageName + ')'"
                [matMenuTriggerFor]="langMenu"
                [disabled]="translating || loading"
            >
                <mat-icon class="icon-size-5 text-gray-100">language</mat-icon>
            </button>
            <button
                *ngIf="hasData"
                type="button"
                class="report-action-btn"
                [matTooltip]="t('View Data')"
                (click)="showDatatable()"
            >
                <mat-icon class="icon-size-5 text-gray-100">table_view</mat-icon>
            </button>
            <button
                *ngIf="hasChart"
                type="button"
                class="report-action-btn"
                [matTooltip]="t('View Chart')"
                (click)="showCharts()"
            >
                <mat-icon
                    class="icon-size-5 text-gray-100"
                    svgIcon="heroicons_outline:chart-pie"
                ></mat-icon>
            </button>
            <button
                type="button"
                class="report-action-btn"
                [matTooltip]="t('Send report via email')"
                (click)="sendEmail()"
            >
                <mat-icon
                    class="icon-size-5 text-gray-100"
                    svgIcon="heroicons_outline:paper-airplane"
                ></mat-icon>
            </button>
            <button
                type="button"
                class="report-action-btn"
                [matTooltip]="t('Download HTML report')"
                (click)="downloadHtmlReport()"
            >
                <mat-icon
                    class="icon-size-5 text-gray-100"
                    svgIcon="heroicons_outline:arrow-down-tray"
                ></mat-icon>
            </button>
        </div>

        <mat-menu
            #langMenu="matMenu"
            class="report-lang-menu"
            xPosition="before"
            yPosition="above"
        >
            <div class="report-lang-menu__header">
                <mat-icon class="report-lang-menu__header-icon">language</mat-icon>
                <span>{{ t('Translate report') }}</span>
            </div>

            <div class="report-lang-menu__list">
                <button
                    *ngFor="let lang of primaryLanguages"
                    type="button"
                    mat-menu-item
                    class="report-lang-menu__item"
                    [class.report-lang-menu__item--active]="currentLang === lang.code"
                    (click)="setLanguage(lang.code)"
                >
                    <span class="report-lang-menu__name">{{ lang.name }}</span>
                    <span class="report-lang-menu__native">{{ lang.native }}</span>
                    <svg
                        *ngIf="currentLang === lang.code"
                        class="report-lang-menu__check"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.42z" />
                    </svg>
                </button>

                <div class="report-lang-menu__divider" role="separator"></div>

                <button
                    *ngFor="let lang of otherLanguages"
                    type="button"
                    mat-menu-item
                    class="report-lang-menu__item"
                    [class.report-lang-menu__item--active]="currentLang === lang.code"
                    (click)="setLanguage(lang.code)"
                >
                    <span class="report-lang-menu__name">{{ lang.name }}</span>
                    <span class="report-lang-menu__native">{{ lang.native }}</span>
                    <svg
                        *ngIf="currentLang === lang.code"
                        class="report-lang-menu__check"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.42z" />
                    </svg>
                </button>
            </div>
        </mat-menu>
    </ng-container>
</div>
